import React, { useEffect, useMemo, useRef, useState } from "react";
import { getUsers, updateUserRole } from "../../services/apiService";
import { useAuth } from "../../context/AuthContext";
import WorkbenchShell from "../workbench/WorkbenchShell";
import { StatusCell } from "../workbench/WorkbenchStatusBar";
import "./adminShell.css";
import "./AdminTable.css";

const ROLE_OPTIONS = [
  { value: "none", label: "No Access" },
  { value: "estimator", label: "Estimator" },
  { value: "admin", label: "Admin" },
];

const roleLabel = (role) =>
  ROLE_OPTIONS.find((option) => option.value === (role || "none"))?.label ||
  "No Access";

/**
 * User management, in the Workbench shell.
 *
 *   rail    the directory roster (search + who is who)
 *   centre  the selected person's access
 *
 * `updateUserRole` semantics are untouched: same call, same refresh, same
 * success and error copy, same "you cannot change your own role" guard.
 */
function UserAdminPage() {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [updatingUserId, setUpdatingUserId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const successTimerRef = useRef(null);
  const { currentUser } = useAuth() || {};

  const fetchUsers = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getUsers();
      setUsers(data.users || []);
    } catch (err) {
      console.error("Failed to fetch organization users:", err);
      setError(
        err.message ||
          "Failed to fetch organization users. Check Graph access and admin permissions.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) {
        window.clearTimeout(successTimerRef.current);
      }
    };
  }, []);

  const showSuccess = (message) => {
    setSuccessMessage(message);
    if (successTimerRef.current) {
      window.clearTimeout(successTimerRef.current);
    }
    successTimerRef.current = window.setTimeout(() => {
      setSuccessMessage("");
      successTimerRef.current = null;
    }, 3000);
  };

  const filteredUsers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return users;

    return users.filter((user) =>
      [user.display_name, user.email, user.user_principal_name]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query)),
    );
  }, [searchQuery, users]);

  const handleRoleChange = async (user, nextRole) => {
    setUpdatingUserId(user.id);
    setError(null);

    try {
      await updateUserRole(user.id, nextRole);
      showSuccess(
        nextRole === "none"
          ? `Removed CostPilot access for ${user.email}`
          : `Updated ${user.email} to ${nextRole}`,
      );
      await fetchUsers();
    } catch (err) {
      setError(err.message || "Failed to update access role");
    } finally {
      setUpdatingUserId(null);
    }
  };

  if (isLoading) {
    return (
      <WorkbenchShell
        variant="admin"
        title="Users"
        center={<p className="wb-empty">Loading organization users...</p>}
      />
    );
  }

  const selectedUser =
    users.find((user) => user.id === selectedUserId) ||
    filteredUsers[0] ||
    null;
  const selectedIsCurrentUser =
    Boolean(currentUser?.entra_object_id) &&
    currentUser.entra_object_id === selectedUser?.id;

  const rail = (
    <div className="user-rail">
      <input
        type="search"
        className="wb-input user-rail__search"
        value={searchQuery}
        onChange={(event) => setSearchQuery(event.target.value)}
        placeholder="Search by name or email"
        aria-label="Search organization users"
      />

      <div className="user-rail__list">
        {filteredUsers.length === 0 ? (
          <p className="wb-empty">No organization users matched your search</p>
        ) : (
          filteredUsers.map((user) => {
            const isCurrentUser =
              currentUser?.entra_object_id &&
              currentUser.entra_object_id === user.id;
            return (
              <button
                key={user.id}
                type="button"
                onClick={() => setSelectedUserId(user.id)}
                className={`wb-item${
                  selectedUser?.id === user.id ? " wb-item--on" : ""
                }`}
              >
                <span className="wb-item__t">
                  <span>
                    {user.display_name}
                    {isCurrentUser ? (
                      <span className="you-badge"> (You)</span>
                    ) : null}
                  </span>
                </span>
                <span className="wb-item__d">
                  {user.email || user.user_principal_name || "—"}
                </span>
                <span className="wb-item__m">{roleLabel(user.role)}</span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );

  const center = (
    <div className="user-detail">
      {error ? (
        <div
          className="user-detail__alert user-detail__alert--error"
          role="alert"
        >
          {error}
        </div>
      ) : null}

      {successMessage ? (
        <div
          className="user-detail__alert user-detail__alert--good"
          role="status"
        >
          {successMessage}
        </div>
      ) : null}

      {selectedUser ? (
        <>
          <header className="user-detail__hd">
            <h3>{selectedUser.display_name}</h3>
            <p>
              {selectedUser.email || selectedUser.user_principal_name || "—"}
            </p>
          </header>

          <dl className="user-detail__grid">
            <div className="user-detail__row">
              <dt>Directory status</dt>
              <dd>{selectedUser.account_enabled ? "Enabled" : "Disabled"}</dd>
            </div>
            <div className="user-detail__row">
              <dt>CostPilot access</dt>
              <dd>
                <select
                  value={selectedUser.role || "none"}
                  onChange={(event) =>
                    handleRoleChange(selectedUser, event.target.value)
                  }
                  disabled={
                    selectedIsCurrentUser || updatingUserId === selectedUser.id
                  }
                  className={`role-select role-${selectedUser.role || "none"}`}
                  title={
                    selectedIsCurrentUser
                      ? "You cannot change your own role from this screen"
                      : ""
                  }
                >
                  {ROLE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {updatingUserId === selectedUser.id ? (
                  <span className="updating-indicator"> Updating...</span>
                ) : null}
              </dd>
            </div>
          </dl>

          {selectedIsCurrentUser ? (
            <p className="user-detail__note">
              This is you. Another admin has to change your own access.
            </p>
          ) : null}
        </>
      ) : (
        <p className="wb-empty">Pick someone from the roster.</p>
      )}

      <section className="admin-info-box">
        <h3>About CostPilot Access</h3>
        <ul>
          <li>
            <strong>Admin</strong> - Full access to CostPilot admin functions
            and estimator workflows.
          </li>
          <li>
            <strong>Estimator</strong> - Standard estimating access without
            admin management rights.
          </li>
          <li>
            <strong>No Access</strong> - The user stays in the organization
            directory but cannot open CostPilot.
          </li>
        </ul>
        <p className="admin-info-box__foot">
          This page manages Entra app-role assignments for the CostPilot app
          registration. It does not create or delete organization accounts.
        </p>
      </section>
    </div>
  );

  return (
    <WorkbenchShell
      variant="admin"
      title="Users"
      subtitle="CostPilot access via Island Millwork Microsoft accounts"
      actions={
        <button type="button" className="wb-tbtn" onClick={fetchUsers}>
          Refresh
        </button>
      }
      railHeader={{ label: "Directory", count: filteredUsers.length }}
      rail={rail}
      centerHeader={{ label: selectedUser?.display_name || "Access" }}
      center={center}
      inspector={null}
      statusBar={
        <>
          <StatusCell label="Directory" value={String(users.length)} />
          <StatusCell
            label="Admins"
            value={String(users.filter((u) => u.role === "admin").length)}
          />
          <StatusCell
            label="Estimators"
            value={String(users.filter((u) => u.role === "estimator").length)}
          />
          <StatusCell
            label="No access"
            value={String(
              users.filter((u) => !u.role || u.role === "none").length,
            )}
          />
        </>
      }
    />
  );
}

export default UserAdminPage;
